<template>
  <div class="chart-container">
    <div v-if="size !== 'mini'" class="chart-legend-container" style="min-height: 56px;">
      <label style="margin-right: 10px;"><span class="chart-mark chart-mark-count"></span><span
              class="chart-legend-name">件数</span></label>
      <label v-if="dist.results.length > 0" v-for="(yAvg, index) in dist.results[0].value.yAvg">
        <span class="chart-mark chart-mark-y-avg"
              :style="{ backgroundColor: lineColors[index + 1].pointBackgroundColor }"></span>
        <span class="chart-legend-name" v-if="categoryId === 'CLF'">「{{ yName }} = {{ yAvg.name }}」の発生率</span>
        <span class="chart-legend-name" v-else>「{{ yName }}」の平均値</span>
        <el-switch class="chart-legend-switch" v-model="switches[index]"></el-switch>
      </label>
    </div>
    <div class="chart-main-container">
      <chart ref="chart" chartType="bar" chartId="distChart" :width="836" :height="294"></chart>
    </div>
    <div class="chart-column-container" v-if="dist.results.length > 1 && size !== 'mini'">
      <div style="display: flex; width: 100%;">
        <el-radio v-for="(result, index) in dist.results" v-model="selectedColumnIndex" name="dist-column"
                  :label="index">{{ result.name }}
        </el-radio>
      </div>
    </div>
  </div>
</template>

<script lang="js">
import { FigurePreviewDist, FigurePreviewDistResult } from '../../defines/model/figure'
import { GraphBarColorSet, GraphLineColorSets } from '../../defines/chart-color'
import CommonUtil from '../../utils/common'
import ChartCmp from './base.vue'
import { Category } from '../../defines/const'
import FormatUtil from '../../utils/format'

export default {
  name: 'DistChartCmp',
  components: {
    'chart': ChartCmp
  },
  props: {
    dist: {},
    yName: '',
    categoryId: '',
    size: '',
    initialColumnIndex: {type: Number, default: 0}
  },
  data () {
    return {
      switches: [],
      selectedColumnIndex: -1,
      selectedDistResult: FigurePreviewDistResult,
      lineColors: GraphLineColorSets
    }
  },
  created () {
    let tmpSwitches = Array(this.dist.results[0].value.yAvg.length)
    for (let index = 0; index < tmpSwitches.length; index++) {
      tmpSwitches[index] = true
    }
    this.selectedColumnIndex = this.initialColumnIndex
    this.switches = tmpSwitches
    this.$nextTick(() => {
      this.$forceUpdate()
    })
  },
  watch: {
    switches (newVal) {
      const chart = this.$refs.chart
      if (CommonUtil.isNull(chart)) return

      for (let index in newVal) {
        if (!chart.nativeChart) continue
        const meta = chart.getDatasetMeta(Number(index))
        meta.hidden = !newVal[index]
        chart.update()
      }
    },
    selectedColumnIndex (newIndex) {
      if (newIndex < 0) return
      this.selectedDistResult = this.dist.results[newIndex]
    },
    selectedDistResult (distData) {
      if (!distData) return

      let datasets = []
      const categoryId = this.categoryId
      let yAvgLabel = categoryId === Category.CLF ? '予測対象の発生率' : '予測対象の平均値'
      const isMini = this.size === 'mini'
      let minValue = null
      let maxValue = null

      for (let index in distData.value.yAvg) {
        const yAvg = distData.value.yAvg[Number(index)].value
        if (categoryId === Category.CLF) {
          const tmpMinValue = yAvg.reduce((a, b) => a < b ? a : b)
          const tmpMaxValue = yAvg.reduce((a, b) => a > b ? a : b)
          if (Number(index) === 0) {
            minValue = tmpMinValue
            maxValue = tmpMaxValue
          } else {
            minValue = Math.min(minValue, tmpMinValue)
            maxValue = Math.max(maxValue, tmpMaxValue)
          }
        }
        datasets.push(Object.assign({
          type: 'line',
          label: yAvgLabel,
          data: yAvg,
          yAxisID: 'y_avg',
          fill: false,
          pointBorderWidth: isMini ? 4 : 8,
          lineTension: 0,
          hidden: !this.switches[Number(index)]
        }, GraphLineColorSets[Number(index) + 1]))
      }

      let fraction = 0
      if (categoryId === Category.CLF) {
        let diffValue = Math.abs(maxValue - minValue)
        while (diffValue !== 0 && diffValue < 0.1) {
          fraction++
          diffValue *= 10
        }
      }

      datasets.push(Object.assign({
        type: 'bar',
        label: '件数',
        data: distData.value.count,
        yAxisID: 'count'
      }, GraphBarColorSet.blue))

      const chartData = {
        labels: distData.value.value,
        datasets: datasets
      }

      const options = {
        responsive: false,
        legend: {
          display: false
        },
        scales: {
          xAxes: [
            {
              ticks: {
                beginAtZero: false,
                major: {},
                minor: {},
                maxRotation: 90,
                callback: function (value, index, values) {
                  if (CommonUtil.isString(value) && CommonUtil.lengthBytes(value) > 20) {
                    value = CommonUtil.substringBytes(value, 20) + '…'
                  }
                  return value
                }
              }
            }],
          yAxes: [
            {
              id: 'count',
              type: 'linear',
              position: 'left',
              scaleLabel: {
                display: !isMini,
                labelString: '件数',
                fontSize: 16
              },
              ticks: {
                beginAtZero: false,
                major: {},
                minor: {},
                callback: function (value, index, values) {
                  return CommonUtil.isEmpty(value) ? '' : `${FormatUtil.formatNumber(value)}`
                }
              }
            }, {
              id: 'y_avg',
              type: 'linear',
              position: 'right',
              scaleLabel: {
                display: !isMini,
                labelString: yAvgLabel,
                fontSize: 16
              },
              gridLines: {
                drawOnChartArea: false
              },
              ticks: {
                beginAtZero: false,
                major: {},
                minor: {},
                callback: function (value, index, values) {
                  if (CommonUtil.isEmpty(value)) {
                    return ''
                  }

                  if (categoryId === Category.CLF) {
                    return FormatUtil.percentage(value, fraction) + '%'
                  } else {
                    return value
                  }
                }
              }
            }]
        },
        tooltips: {
          callbacks: {
            title: function (tooltipItems, data) {
              const tooltipItem = tooltipItems[0]
              return data.labels[tooltipItem.index]
            },
            label: function (tooltipItem, data) {
              const datasetIndex = tooltipItem.datasetIndex
              let label = data.datasets[datasetIndex].label || ''

              if (CommonUtil.isNotEmpty(label)) {
                label += ': '
              }
              if (categoryId !== Category.CLF || datasetIndex >= (datasets.length - 1)) {
                label += FormatUtil.formatNumber(tooltipItem.yLabel)
              } else {
                label += FormatUtil.percentage(tooltipItem.yLabel, fraction) + '%'
              }

              return label
            }
          }
        }
      }
      if (this.categoryId === 'UNS') {
        options.scales.yAxes = options.scales.yAxes.filter(y => y.id !== 'y_avg')
      }
      this.$refs.chart.renderChart(chartData, options)
    }
  }
}
</script>

<style scoped>
  .chart-container {
    display: flex;
    flex-direction: column;
  }

  .chart-legend-container {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    overflow-y: auto;
  }

  .chart-legend-container > label {
    display: flex;
    align-items: center;
  }

  .chart-mark {
    display: inline-block;
    width: 12px;
    height: 12px;
  }

  .chart-legend-container > label:not(:first-child) {
    cursor: pointer;
  }

  .chart-mark-count {
    background-color: #86C7F5;
    margin-right: 6px;
  }

  .chart-mark-y-avg {
    border-radius: 50%;
  }

  .chart-legend-switch {
    transform: scale(0.6, 0.6);
  }

  .chart-main-container {
    display: flex;
    justify-content: center;
    flex-grow: 1;
  }

  .chart-column-container {
    margin-top: 20px;
    overflow-x: scroll;
    overflow-y: hidden;
    display: flex;
    flex-wrap: nowrap;
    flex-shrink: 0;
    padding: 0 24px;
  }

  .chart-column-container > * {
    margin: 12px 6px;
  }
</style>



// WEBPACK FOOTER //
// src/components/chart/dist.vue